/**
 * Public profiles: doctor self-service, clinic-admin listing, super-admin verification queue, and the
 * anonymous directory. Mirrors medopd-backend's publicprofile package (PublicProfileDtos).
 */
import { fetchApi, fetchGetFileApi, fetchMultipartApi } from '../client';

export type VerificationStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED' | 'REVOKED';
export type PublicationStatus = 'DRAFT' | 'PUBLISHED' | 'UNPUBLISHED' | 'SUSPENDED';

export interface Qualification {
  degree: string;
  specialty?: string | null;
  institution?: string | null;
  yearAwarded?: number | null;
}

export interface ListingView {
  doctorId: string;
  clinicName: string;
  clinicCity?: string | null;
  listed: boolean;
  showFee: boolean;
  clinicPublished: boolean;
  clinicEntitled: boolean;
}

/** A submitted About text waiting for ClinVea review (medopd-docs 144, D3). `about` is what is public. */
export interface AboutReview {
  status: 'PENDING' | 'REJECTED';
  pendingText: string;
  note?: string | null;
  submittedAt?: string | null;
}

/** One row of the super-admin About-text queue. */
export interface AboutTextQueueItem {
  kind: 'DOCTOR' | 'CLINIC';
  profileId: string;
  name?: string | null;
  currentText?: string | null;
  pendingText: string;
  flags: string[];
  submittedAt?: string | null;
}

export interface DoctorProfileView {
  slug: string;
  publicPath: string;
  displayName: string;
  primarySpecialization?: string | null;
  about?: string | null;
  aboutReview?: AboutReview | null;
  practiceStartYear?: number | null;
  yearsOfExperience?: number | null;
  languages: string[];
  services: string[];
  teleconsultOffered: boolean;
  qualifications: Qualification[];
  registrationNumber?: string | null;
  registrationCouncil?: string | null;
  registrationYear?: number | null;
  verificationStatus: VerificationStatus;
  latestVerification?: { id: string; status: string; submittedAt?: string; reviewedAt?: string; decisionNote?: string | null } | null;
  publicationStatus: PublicationStatus;
  consentRecorded: boolean;
  consentCurrent: boolean;
  consentVersion: string;
  directoryOptOut: boolean;
  listings: ListingView[];
  publishBlockers: string[];
  publiclyVisible: boolean;
  /** Optional; only used for the public search's "doctor's gender" filter. */
  gender?: 'female' | 'male' | 'other' | null;
}

/** All optional; omitted = unchanged. Registration fields are accepted from the doctor only. */
export interface DoctorProfileUpdate {
  displayName?: string;
  primarySpecialization?: string;
  about?: string;
  practiceStartYear?: number | null;
  languages?: string[];
  services?: string[];
  teleconsultOffered?: boolean;
  qualifications?: Qualification[];
  registrationNumber?: string;
  registrationCouncil?: string;
  registrationYear?: number | null;
  slug?: string;
  /** '' clears it. */
  gender?: 'female' | 'male' | 'other' | '';
}

export interface OpeningHours { dayOfWeek: number; opens: string; closes: string }

export interface ClinicDoctorStatus {
  doctorId: string;
  name: string;
  specialization?: string | null;
  hasProfile: boolean;
  verificationStatus?: VerificationStatus | null;
  publicationStatus?: PublicationStatus | null;
  listedHere: boolean;
  doctorUserId?: string | null;
}

export interface ClinicProfileView {
  slug: string;
  publicPath: string;
  clinicName: string;
  city?: string | null;
  address?: string | null;
  about?: string | null;
  aboutReview?: AboutReview | null;
  locality?: string | null;
  amenities: string[];
  paymentModes: string[];
  openingHours: OpeningHours[];
  publicationStatus: PublicationStatus;
  directoryOptOut: boolean;
  /** Verified patient reviews shown on this clinic's doctors' public pages (clinic opt-in). */
  reviewsEnabled: boolean;
  entitled: boolean;
  onlineBookingEnabled: boolean;
  publishBlockers: string[];
  doctors: ClinicDoctorStatus[];
  /** Self-declared accreditations, shown as such on the public page. */
  accreditations?: Accreditation[];
  /** WhatsApp Business number (digits with country code) behind the public "Chat on WhatsApp" button, or null. */
  whatsappNumber?: string | null;
}

export type AccreditationType = 'NABL' | 'NABH' | 'JCI' | 'ISO' | 'OTHER';
export interface Accreditation { type: AccreditationType; number: string; issuedBy?: string; validTill?: string }

export interface ClinicProfileUpdate {
  about?: string;
  locality?: string;
  amenities?: string[];
  paymentModes?: string[];
  openingHours?: OpeningHours[];
  slug?: string;
  accreditations?: Accreditation[];
  /** "" clears; omit to leave unchanged. */
  whatsappNumber?: string;
}

export interface VerificationQueueItem {
  requestId: string;
  doctorName: string;
  primarySpecialization?: string | null;
  registrationNumber: string;
  registrationCouncil: string;
  registrationCouncilName?: string | null;
  registrationYear?: number | null;
  submittedAt: string;
  hasDocument: boolean;
  scanStatus: string;
  provider: string;
  clinics: string[];
  previousRequests: number;
  /** Which documents came with the request: REGISTRATION, IDENTITY, ESTABLISHMENT. */
  documents?: VerificationDocumentKind[];
  /** Listed themselves on ClinVea Care — no clinic stands behind them, so check the identity proof. */
  selfRegistered?: boolean;
}

export type VerificationDocumentKind = 'REGISTRATION' | 'IDENTITY' | 'ESTABLISHMENT';

export interface PageOf<T> { items: T[]; page: number; size: number; total: number }

export interface PublicProfileCatalog {
  /** Every registration council (medical first, then dental) — the name predates dental councils. */
  medicalCouncils: Record<string, string>;
  /** The dental councils among them (newer servers), for grouping pickers. */
  dentalCouncils?: Record<string, string>;
  languages: Record<string, string>;
  amenities: Record<string, string>;
  paymentModes: Record<string, string>;
}

const json = (method: string, body?: unknown) => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });

// ─── Reviews ────────────────────────────────────────────────────────────────────────────────────

export interface ClinicReviewItem {
  id: string;
  doctorName?: string | null;
  displayName: string;
  recommends: boolean;
  text: string;
  visitMonth: string;
  publishedAt?: string | null;
  reply?: string | null;
  replyStatus?: 'PENDING' | 'PUBLISHED' | 'REJECTED' | null;
  reported: boolean;
}

/** The published content policy — the only grounds a review may be rejected on. */
export const REJECTION_REASONS = [
  'PERSONAL_OR_HEALTH_DETAILS', 'CONTACT_DETAILS_OR_LINKS', 'ABUSIVE_OR_HATEFUL', 'NOT_ABOUT_THE_CARE',
  'CONFLICT_OF_INTEREST', 'SPAM', 'OTHER',
] as const;
export type RejectionReason = typeof REJECTION_REASONS[number];

export interface ModerationItem {
  id: string;
  kind: 'REVIEW' | 'REPLY' | 'REPORT';
  clinicName?: string | null;
  doctorName?: string | null;
  displayName: string;
  recommends: boolean;
  text?: string | null;
  flags: string[];
  status: string;
  reply?: string | null;
  replyStatus?: string | null;
  replyFlags: string[];
  reportedAt?: string | null;
  reportReason?: string | null;
  createdAt?: string | null;
}

export interface ModerationPage {
  items: ModerationItem[];
  page: number;
  size: number;
  total: number;
}

// ─── Photos (Phase 12) ──────────────────────────────────────────────────────────────────────────

export type PhotoStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REMOVED';

/** An owner's own photo: a doctor's portrait or one of the clinic's gallery photos. */
export interface PhotoView {
  id: string;
  kind: 'PORTRAIT' | 'GALLERY';
  status: PhotoStatus;
  caption?: string | null;
  width: number;
  height: number;
  uploadedAt: string;
  reviewedAt?: string | null;
  rejectReason?: string | null;
}

export interface PhotoModerationItem {
  id: string;
  ownerType: 'DOCTOR' | 'CLINIC';
  ownerName: string;
  clinicName?: string | null;
  kind: 'PORTRAIT' | 'GALLERY';
  caption?: string | null;
  noPatientsConfirmed: boolean;
  width: number;
  height: number;
  byteSize: number;
  uploadedAt: string;
  status: PhotoStatus;
  rejectReason?: string | null;
}

/** Upload limits the server enforces; checked here too so people get the message before uploading. */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const PHOTO_TYPES = 'image/jpeg,image/png';
export const CLINIC_MAX_PHOTOS = 8;

const photoForm = (file: File, extra: Record<string, string> = {}) => {
  const form = new FormData();
  form.append('photo', file);
  Object.entries(extra).forEach(([k, v]) => form.append(k, v));
  return form;
};

export const publicProfileModule = {
  catalog: () => fetchApi<PublicProfileCatalog>('/api/public/directory/catalog'),

  doctor: {
    get: () => fetchApi<DoctorProfileView>('/api/doctor/public-profile'),
    update: (data: DoctorProfileUpdate) => fetchApi<DoctorProfileView>('/api/doctor/public-profile', json('PUT', data)),
    consent: () => fetchApi<DoctorProfileView>('/api/doctor/public-profile/consent', json('POST')),
    publish: () => fetchApi<DoctorProfileView>('/api/doctor/public-profile/publish', json('POST')),
    unpublish: () => fetchApi<DoctorProfileView>('/api/doctor/public-profile/unpublish', json('POST')),
    setListing: (doctorId: string, listed?: boolean, showFee?: boolean) =>
      fetchApi<DoctorProfileView>('/api/doctor/public-profile/listings', json('PUT', { doctorId, listed, showFee })),
    setDirectoryOptOut: (optOut: boolean) =>
      fetchApi<DoctorProfileView>('/api/doctor/public-profile/directory-opt-out', json('PUT', { optOut })),
    /** Identity proof is required for self-registered doctors; establishment proof is optional. */
    submitVerification: (certificate: File, identity?: File | null, establishment?: File | null) => {
      const form = new FormData();
      form.append('certificate', certificate);
      if (identity) form.append('identity', identity);
      if (establishment) form.append('establishment', establishment);
      return fetchMultipartApi<DoctorProfileView>('/api/doctor/public-profile/verification', form);
    },
  },

  clinic: {
    get: () => fetchApi<ClinicProfileView>('/api/clinic/public-profile'),
    update: (data: ClinicProfileUpdate) => fetchApi<ClinicProfileView>('/api/clinic/public-profile', json('PUT', data)),
    publish: () => fetchApi<ClinicProfileView>('/api/clinic/public-profile/publish', json('POST')),
    unpublish: () => fetchApi<ClinicProfileView>('/api/clinic/public-profile/unpublish', json('POST')),
    setDirectoryOptOut: (optOut: boolean) =>
      fetchApi<ClinicProfileView>('/api/clinic/public-profile/directory-opt-out', json('PUT', { optOut })),
    setReviewsEnabled: (enabled: boolean) =>
      fetchApi<ClinicProfileView>('/api/clinic/public-profile/reviews', json('PUT', { enabled })),
    getDoctor: (doctorUserId: string) =>
      fetchApi<DoctorProfileView>(`/api/clinic/public-profile/doctors/${encodeURIComponent(doctorUserId)}`),
    updateDoctor: (doctorUserId: string, data: DoctorProfileUpdate) =>
      fetchApi<DoctorProfileView>(`/api/clinic/public-profile/doctors/${encodeURIComponent(doctorUserId)}`, json('PUT', data)),
  },

  /** Published reviews at the active clinic: all of them for clinic admins, own for doctors. Reply/report only. */
  reviews: {
    list: (page = 0, size = 20) => fetchApi<PageOf<ClinicReviewItem>>(`/api/reviews?page=${page}&size=${size}`),
    reply: (id: string, text: string) =>
      fetchApi<void>(`/api/reviews/${encodeURIComponent(id)}/reply`, json('POST', { text })),
    report: (id: string, reason: string) =>
      fetchApi<void>(`/api/reviews/${encodeURIComponent(id)}/report`, json('POST', { reason })),
  },

  /** Super-admin review of doctor and clinic About text (medopd-docs 144, D3). */
  aboutText: {
    queue: () => fetchApi<AboutTextQueueItem[]>('/api/admin/about-text'),
    /** @param overrideReason needed when the automatic check asked for one (medopd-docs 146) */
    approve: (kind: AboutTextQueueItem['kind'], profileId: string, overrideReason?: string) =>
      fetchApi<void>(`/api/admin/about-text/${kind.toLowerCase()}/${encodeURIComponent(profileId)}/approve`,
        json('POST', overrideReason ? { overrideReason } : undefined)),
    reject: (kind: AboutTextQueueItem['kind'], profileId: string, note: string) =>
      fetchApi<void>(`/api/admin/about-text/${kind.toLowerCase()}/${encodeURIComponent(profileId)}/reject`, json('POST', { note })),
  },

  /** Super-admin review moderation. */
  moderation: {
    queue: (page = 0, size = 20) => fetchApi<ModerationPage>(`/api/admin/reviews?page=${page}&size=${size}`),
    approve: (id: string, overrideReason?: string) => fetchApi<void>(`/api/admin/reviews/${encodeURIComponent(id)}/approve`,
      json('POST', overrideReason ? { overrideReason } : undefined)),
    reject: (id: string, reason: RejectionReason, note?: string) =>
      fetchApi<void>(`/api/admin/reviews/${encodeURIComponent(id)}/reject`, json('POST', { reason, note })),
    dismissReport: (id: string, note?: string) =>
      fetchApi<void>(`/api/admin/reviews/${encodeURIComponent(id)}/dismiss-report`, json('POST', { note })),
    approveReply: (id: string, overrideReason?: string) => fetchApi<void>(`/api/admin/reviews/${encodeURIComponent(id)}/reply/approve`,
      json('POST', overrideReason ? { overrideReason } : undefined)),
    rejectReply: (id: string, note?: string) =>
      fetchApi<void>(`/api/admin/reviews/${encodeURIComponent(id)}/reply/reject`, json('POST', { note })),
  },

  /** Photos for public pages. Every upload waits for a ClinVea review before it appears. */
  photos: {
    doctor: {
      list: () => fetchApi<PhotoView[]>('/api/doctor/public-profile/photos'),
      upload: (file: File) => fetchMultipartApi<PhotoView>('/api/doctor/public-profile/photos', photoForm(file)),
      remove: (id: string) => fetchApi<void>(`/api/doctor/public-profile/photos/${encodeURIComponent(id)}`, json('DELETE')),
      image: (id: string) => fetchGetFileApi(`/api/doctor/public-profile/photos/${encodeURIComponent(id)}/image`),
    },
    clinic: {
      list: () => fetchApi<PhotoView[]>('/api/clinic/public-profile/photos'),
      upload: (file: File, caption: string, noPatientsConfirmed: boolean) =>
        fetchMultipartApi<PhotoView>('/api/clinic/public-profile/photos',
          photoForm(file, { caption, noPatientsConfirmed: String(noPatientsConfirmed) })),
      remove: (id: string) => fetchApi<void>(`/api/clinic/public-profile/photos/${encodeURIComponent(id)}`, json('DELETE')),
      image: (id: string) => fetchGetFileApi(`/api/clinic/public-profile/photos/${encodeURIComponent(id)}/image`),
      /** Every waiting and live photo, in the new page order. */
      reorder: (photoIds: string[]) =>
        fetchApi<PhotoView[]>('/api/clinic/public-profile/photos/order', json('PUT', { photoIds })),
    },
    moderation: {
      queue: (status: PhotoStatus = 'PENDING') =>
        fetchApi<PhotoModerationItem[]>(`/api/admin/public-photos?status=${encodeURIComponent(status)}`),
      /** One page of a moderation tab, with totals (medopd-docs 112). */
      queuePage: (status: PhotoStatus, page: number, size: number) =>
        fetchApi<import('../../../types/pagination').PagedResponse<PhotoModerationItem>>(
          `/api/admin/public-photos?status=${encodeURIComponent(status)}&page=${page}&size=${size}`),
      image: (id: string) => fetchGetFileApi(`/api/admin/public-photos/${encodeURIComponent(id)}/image`),
      approve: (id: string) => fetchApi<PhotoModerationItem>(`/api/admin/public-photos/${encodeURIComponent(id)}/approve`, json('POST')),
      reject: (id: string, reason: string) =>
        fetchApi<PhotoModerationItem>(`/api/admin/public-photos/${encodeURIComponent(id)}/reject`, json('POST', { reason })),
    },
  },

  verification: {
    pending: (page = 0, size = 20) =>
      fetchApi<PageOf<VerificationQueueItem>>(`/api/admin/doctor-verifications?page=${page}&size=${size}`),
    documentLink: (requestId: string) =>
      fetchApi<{ url: string; expiresInSeconds: number; contentType: string }>(
        `/api/admin/doctor-verifications/${encodeURIComponent(requestId)}/document`),
    /** A short-lived link to one document on the request. Audited. */
    documentLinkOf: (requestId: string, kind: VerificationDocumentKind) =>
      fetchApi<{ url: string; expiresInSeconds: number; contentType: string }>(
        `/api/admin/doctor-verifications/${encodeURIComponent(requestId)}/documents/${kind}`),
    approve: (requestId: string, note?: string) =>
      fetchApi<void>(`/api/admin/doctor-verifications/${encodeURIComponent(requestId)}/approve`, json('POST', { note })),
    reject: (requestId: string, note: string) =>
      fetchApi<void>(`/api/admin/doctor-verifications/${encodeURIComponent(requestId)}/reject`, json('POST', { note })),
  },
};
